// @ts-nocheck
/* eslint-enable */
// App
import { renderToStaticMarkup } from 'react-dom/server';

// Legacy controller
import { getFaqSchemaTemplate } from 'app/shared/modules/FaqSection/controller';

// Lodash
import omitBy from 'lodash/omitBy';
import isNull from 'lodash/isNull';
import map from 'lodash/map';

// Misc / utils
import QA_SCHEMA_DATA from 'app/shared/utils/quickAnswers.json';
import { DENVER_QUICK_ANSWER } from 'app/shared/utils/quickAnswersV2';
import routeUtils from 'app/shared/utils/routeUtils';
import dateUtils from 'app/shared/utils/dateUtils';

const _schemaHelpers = {
  _getFormattedFaqAnswer(answer, answerType, listHeader = null) {
    const listText = (type, header) => {
      const ListElement = type === 'unorderedList' ? 'ul' : 'ol';

      if (header) {
        return `${header}<${ListElement}>${answer.map((item) => {
          return `<li>${item}</li>`;
        })}</${ListElement}>`;
      }

      return `<${ListElement}>${answer.map((item) => {
        return `<li>${item}</li>`;
      })}</${ListElement}>`;
    };

    return answerType === 'answer'
      ? {
          '@type': 'Answer',
          text: answer,
        }
      : {
          '@type': 'Answer',
          text: listText(answerType, listHeader),
        };
  },
  _getAbout(listings, houseOrApartment) {
    return listings.map((listing) => {
      return {
        '@type': houseOrApartment,
        '@id': `https://hotpads.com${listing.uriV2}`,

        url: `https://hotpads.com${listing.uriV2}`,
        image: listing.previewPhoto.url,
        name: listing.displayName,
        address: {
          '@type': 'PostalAddress',
          streetAddress: listing.address.street,
          addressLocality: listing.address.city,
          addressRegion: listing.address.state,
          postalCode: listing.address.zip,
          addressCountry: {
            '@type': 'Country',
            name: 'US',
          },
        },
      };
    });
  },
  _getBreadcrumb(breadcrumbs = [], pathname, searchSlug, position) {
    return breadcrumbs.length === 0 || routeUtils.isNearMeUrl(pathname)
      ? null
      : {
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map((crumb) => {
            const uri = `/${crumb.resourceId}/${searchSlug}`;
            const { name } = crumb;

            return {
              '@type': 'ListItem',
              position: ++position,
              item: {
                '@type': 'Thing',
                '@id': uri,
                name,
              },
            };
          }),
        };
  },
  _getContentLocation(area) {
    return {
      '@type': 'City',
      name: area.city,
      containedIn: {
        '@type': 'State',
        name: area.state,
      },
    };
  },
  /**
   *
   * _getFaqPageMainEntity is responsible for building the FAQPage schema utilized by Google
   * which manifests itself in Google's SERPs. This looks like an additional drop-down with
   * FAQ data right below the search engine result (i.e. query san francisco apartments for rent)
   *
   * This function depends on the data shape found in quickAnswers.json. The JSON shape is as
   * follows:
   *
   * AREA_ID[string]: {
   *     url: AREA_URL[string],
   *     questions: [
   *         {
   *             question: QUESTION[string]
   *             answerType: "answer"[string] | "orderedList"[string] | "unorderedList"[string]
   *             answer: QUESTION_ANSWER[string]|[array]
   *         }
   *     ]
   * }
   *
   * If answerType is `orderedList`, the function will return an ordered list with the array data as the answer.
   * If answerType is `unorderedList`, the function will return an unordered list with the array data as the answer.
   * If answerType is `answer`, the function will simply return the string data as the answer.
   */
  _getFaqPageMainEntity(area, facts, factsByBeds, searchSlug, totalListings) {
    const areaFaqData = getFaqSchemaTemplate({
      searchSlug,
      data: { area, facts, factsByBeds, totalListings },
    });
    const areaFaqQuestionsArray = areaFaqData && areaFaqData.data;
    const formattedSchemaObject = (questionAnswerCluster) => {
      const question = questionAnswerCluster.question;
      const answer = questionAnswerCluster.answer;
      const answerType = questionAnswerCluster.answerType;
      const listHeader = questionAnswerCluster.listHeader || null;

      return {
        '@type': 'Question',
        name: question,
        acceptedAnswer: _schemaHelpers._getFormattedFaqAnswer(answer, answerType, listHeader),
      };
    };

    const dynamicContent = areaFaqQuestionsArray
      ? areaFaqQuestionsArray.map((questionAnswerCluster) => {
          return formattedSchemaObject(questionAnswerCluster);
        })
      : null;

    const hardcodedContent =
      QA_SCHEMA_DATA[area.id] && searchSlug === 'apartments-for-rent'
        ? map(QA_SCHEMA_DATA[area.id].questions, (questionAnswerCluster) => {
            return formattedSchemaObject(questionAnswerCluster);
          })
        : null;

    // eslint-disable-next-line no-unused-expressions
    dynamicContent && dynamicContent.push(hardcodedContent); // Faster than using .concat()

    return dynamicContent ? dynamicContent : hardcodedContent;
  },
  _getFaqPageMainEntityV2() {
    const hardcodedContent = DENVER_QUICK_ANSWER.questions.map((questionAnswerCluster) => {
      const question = renderToStaticMarkup(questionAnswerCluster.question);
      const answer = renderToStaticMarkup(questionAnswerCluster.answer);

      return {
        '@type': 'Question',
        name: question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: answer,
        },
      };
    });

    return hardcodedContent;
  },
  _getMainContentOfPage() {
    const selectors = [];

    if (typeof document !== 'undefined') {
      if (document.querySelector('#area-title')) {
        selectors.push('#area-title');
      }

      if (selectors.length === 0) {
        return null;
      }

      return {
        '@type': 'WebPageElement',
        cssSelector: selectors,
      };
    }
    return null;
  },
  _getRelatedLink(seoLinks) {
    const relatedLink = seoLinks.map((link) => {
      const links = link.links;
      return links.map((subLink) => {
        return `https://hotpads.com${subLink.uriV2}`;
      });
    });

    const reducedLinks = relatedLink.reduce((groupOfLinks, link) => {
      return groupOfLinks.concat(link);
    }, []);

    return reducedLinks;
  },
  _getReviewedBy() {
    return {
      '@type': 'Organization',
      name: 'HotPads.com',
    };
  },
  _getSpeakable() {
    const selectors = [];

    if (typeof document !== 'undefined') {
      if (document.querySelector('#faq-section')) {
        selectors.push('#faq-section');
      }

      if (document.querySelector('#article-section')) {
        selectors.push('#article-section');
      }

      if (selectors.length === 0) {
        return null;
      }

      return {
        '@type': 'SpeakableSpecification',
        cssSelector: selectors,
      };
    }
    return null;
  },
  _getSpeciality() {
    return 'Apartment and House Rental Search';
  },
};
/**
 * Handles gathering Head structured data:
 * - Schema.org structured data
 * - Used for google FAQ section
 */
export const useAreaStructuredData = () => {
  const getAreaSchema = ({
    area = {},
    houseOrApartment = 'ApartmentComplex',
    listings = [],
    pathname = '',
    searchSlug = 'apartments-for-rent',
    seoLinks = [],
  }) => {
    const position = 0;
    const contentLocation = _schemaHelpers._getContentLocation(area);
    const mainContentOfPage = _schemaHelpers._getMainContentOfPage();
    const breadcrumb = _schemaHelpers._getBreadcrumb(area.breadcrumbs, pathname, searchSlug, position);
    const speakable = _schemaHelpers._getSpeakable();
    const about = _schemaHelpers._getAbout(listings, houseOrApartment);
    const relatedLink = _schemaHelpers._getRelatedLink(seoLinks);
    const specialty = _schemaHelpers._getSpeciality();
    const reviewedBy = _schemaHelpers._getReviewedBy();

    return omitBy(
      {
        '@context': 'http://schema.org',
        '@type': 'SearchResultsPage',
        contentLocation,
        mainContentOfPage,
        breadcrumb,
        speakable,
        about,
        relatedLink,
        specialty,
        reviewedBy,
      },
      isNull,
    );
  };

  const getAreaFaqPageSchema = ({ area, facts, factsByBeds, searchSlug, totalListings }) => {
    const mainEntity = _schemaHelpers._getFaqPageMainEntity(area, facts, factsByBeds, searchSlug, totalListings);

    if (isNull(mainEntity)) {
      return null;
    }

    return {
      '@context': 'http://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    };
  };

  const getAreaFaqSchemaV2 = () => {
    const mainEntity = _schemaHelpers._getFaqPageMainEntityV2();

    if (isNull(mainEntity)) {
      return null;
    }

    return {
      '@context': 'http://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    };
  };

  const getEventSchema = ({ listing }) => {
    const currentDate = dateUtils.formatDateYyyyMmDd(new Date(), '-');
    return {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: `3D Tour Available - ${listing?.displayName}`,
      description: listing?.fullDescription,
      image: listing?.previewPhotoMed?.url,
      startDate: currentDate,
      endDate: currentDate,
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      performer: {
        '@type': 'Organization',
        name: listing?.broker?.companyName,
      },
      organizer: {
        '@type': 'Organization',
        name: listing?.broker?.companyName,
      },
      offers: {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Online 3D Tour',
        },
        url: listing.uriV2,
        price: 0,
        priceCurrency: 'USD',
        availability: 'https://schema.org/OnlineOnly',
        validFrom: currentDate,
      },
      location: {
        '@type': 'VirtualLocation',
        url: listing?.uriV2,
      },
    };
  };

  return {
    getAreaSchema,
    getAreaFaqPageSchema,
    getAreaFaqSchemaV2,
    getEventSchema,
  };
};

export default useAreaStructuredData;
